import { markUploadDismissed } from "@/router";
import {
  ArrowUpTrayIcon,
  PaintBrushIcon,
  SparklesIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import { Button, Heading, Link, Text } from "@radix-ui/themes";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { FeatureCard } from "./FeatureCard";

interface UploadViewProps {
  isDragOver: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onFileSelect: () => void;
}

export const UploadView = ({
  isDragOver,
  onDragOver,
  onDragLeave,
  onDrop,
  onFileSelect,
}: UploadViewProps) => {
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <div className="text-center space-y-2">
        <Heading size="7">{t("onboarding.upload.title")}</Heading>
        <Text className="text-slate-600 dark:text-slate-400 block">
          {t("onboarding.upload.subtitle")}
        </Text>
      </div>

      <div
        className={twMerge(
          "border-2 border-dashed rounded-2xl p-12 md:p-20 text-center transition-all cursor-pointer",
          isDragOver
            ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-950/20"
            : "border-slate-300 dark:border-slate-700 hover:border-indigo-400 hover:bg-slate-50 dark:hover:bg-slate-900"
        )}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={onFileSelect}
      >
        <div className="space-y-4">
          <div className="mx-auto w-16 h-16 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center">
            <ArrowUpTrayIcon className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <Text className="text-lg font-medium block">
              {t("onboarding.upload.drop_hint")}
            </Text>
            <Text className="text-slate-500 dark:text-slate-400 block text-sm">
              {t("onboarding.upload.formats")}
            </Text>
          </div>
          <Button size="3" className="mt-4">
            {t("onboarding.upload.button")}
          </Button>
        </div>
      </div>

      {/* Features preview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-8">
        <FeatureCard
          icon={<SparklesIcon className="w-6 h-6" />}
          title={t("onboarding.features.professional.title")}
          description={t("onboarding.features.professional.description")}
        />
        <FeatureCard
          icon={<PaintBrushIcon className="w-6 h-6" />}
          title={t("onboarding.features.edit.title")}
          description={t("onboarding.features.edit.description")}
        />
        <FeatureCard
          icon={<UserCircleIcon className="w-6 h-6" />}
          title={t("onboarding.features.train.title")}
          description={t("onboarding.features.train.description")}
        />
      </div>

      {/* Skip link */}
      <div className="text-center pt-4">
        <Link
          href="/portraits/"
          onClick={() => markUploadDismissed()}
          className="text-sm text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
        >
          {t("onboarding.upload.skip")}
        </Link>
      </div>
    </motion.div>
  );
};
